import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { Quote, SaveQuoteLine, SaveQuoteRequest } from '../api/types';
import { Alert, Spinner, formatDate, money } from '../components/common';
import { useAuth } from '../state/AuthContext';
import { useBranch } from '../state/BranchContext';

/**
 * Corporate quotes.
 *
 * The workflow exists to enforce one rule: nothing is made before the money
 * lands. Two hundred smoothies is a real cost in fruit and staff hours, and
 * "we'll invoice you" is how a small shop ends up financing somebody else's
 * accounts department. So "Delivered" is refused until the quote is settled,
 * and the reminders chase payment on their own.
 */

const STATUS_TONE: Record<string, string> = {
  Draft: 'pill--muted',
  Sent: 'pill--good',
  Paid: 'pill--good',
  Fulfilled: 'pill--muted',
  Declined: 'pill--bad',
  Expired: 'pill--bad',
};

function emptyLine(): SaveQuoteLine {
  return { description: '', quantity: 1, unitPrice: 0 };
}

function blank(): SaveQuoteRequest {
  return {
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    lines: [emptyLine()],
    branchId: null,
    notes: null,
    discountPercent: 0,
    validUntil: null,
  };
}

export function StaffQuotesPage() {
  const { customer: me } = useAuth();
  const { branches } = useBranch();
  const canEdit = me?.role === 'Admin' || me?.role === 'SuperAdmin';

  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [filter, setFilter] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<SaveQuoteRequest>(blank);

  const load = useCallback(async () => {
    try {
      setQuotes(await api.quotes(filter));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, note?: string) {
    setBusy(true);
    setError(null);
    setMessage(null);

    try {
      const result = await action();
      await load();
      const spoken = (result as { message?: string } | undefined)?.message;
      if (spoken || note) setMessage(spoken ?? note ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const total = form.lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);
  const afterDiscount = total - total * ((form.discountPercent ?? 0) / 100);

  if (!quotes) return <Spinner />;

  return (
    <section className="page">
      <div className="branches__head">
        <div>
          <h1>Corporate quotes</h1>
          <p className="muted">
            Priced, emailed, and settled before anything is made. Unpaid quotes are chased
            automatically — once a day after sending, then every three days, four times at most.
          </p>
        </div>
        {canEdit && editing === null && (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => {
              setForm(blank());
              setEditing('new');
            }}
          >
            New quote
          </button>
        )}
      </div>

      {error && <Alert>{error}</Alert>}
      {message && <Alert tone="success">{message}</Alert>}

      <label className="field field--inline">
        <span>Show</span>
        <select value={filter ?? ''} onChange={(e) => setFilter(e.target.value || null)}>
          <option value="">Everything</option>
          <option value="Draft">Drafts</option>
          <option value="Sent">Awaiting payment</option>
          <option value="Paid">Settled</option>
          <option value="Fulfilled">Delivered</option>
          <option value="Expired">Expired</option>
          <option value="Declined">Declined</option>
        </select>
      </label>

      {editing !== null && (
        <div className="panel">
          <h2 className="panel__title">{editing === 'new' ? 'New quote' : 'Edit quote'}</h2>

          <div className="form-row">
            <label className="field">
              <span>Company</span>
              <input
                value={form.companyName}
                placeholder="Discovery Ltd"
                onChange={(e) => setForm({ ...form, companyName: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Contact</span>
              <input
                value={form.contactName}
                placeholder="Lerato Mkhize"
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              />
            </label>
          </div>

          <div className="form-row">
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={form.contactEmail}
                placeholder="lerato@discovery.co.za"
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Phone</span>
              <input
                type="tel"
                value={form.contactPhone}
                placeholder="011 555 0000"
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
              />
            </label>
          </div>

          <h3 className="section-heading">Lines</h3>

          {form.lines.map((line, index) => (
            <div key={index} className="quoteline">
              <input
                className="quoteline__desc"
                value={line.description}
                placeholder="40 x 500ml smoothies, assorted, delivered 07:30"
                onChange={(e) => {
                  const lines = [...form.lines];
                  lines[index] = { ...line, description: e.target.value };
                  setForm({ ...form, lines });
                }}
              />
              <input
                className="quoteline__qty"
                type="number"
                min="1"
                value={line.quantity}
                onChange={(e) => {
                  const lines = [...form.lines];
                  lines[index] = { ...line, quantity: Number(e.target.value) };
                  setForm({ ...form, lines });
                }}
              />
              <input
                className="quoteline__price"
                type="number"
                step="0.01"
                min="0"
                value={line.unitPrice}
                onChange={(e) => {
                  const lines = [...form.lines];
                  lines[index] = { ...line, unitPrice: Number(e.target.value) };
                  setForm({ ...form, lines });
                }}
              />
              <span className="quoteline__total">{money(line.quantity * line.unitPrice)}</span>
              {form.lines.length > 1 && (
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => setForm({ ...form, lines: form.lines.filter((_, i) => i !== index) })}
                >
                  Remove
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            className="linkbtn"
            onClick={() => setForm({ ...form, lines: [...form.lines, emptyLine()] })}
          >
            + Add a line
          </button>

          <div className="form-row">
            <label className="field">
              <span>Discount %</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={form.discountPercent ?? 0}
                onChange={(e) => setForm({ ...form, discountPercent: Number(e.target.value) })}
              />
            </label>
            <label className="field">
              <span>Valid until</span>
              <input
                type="date"
                value={form.validUntil ?? ''}
                onChange={(e) => setForm({ ...form, validUntil: e.target.value || null })}
              />
            </label>
          </div>

          <label className="field">
            <span>Branch</span>
            <select
              value={form.branchId ?? ''}
              onChange={(e) => setForm({ ...form, branchId: e.target.value || null })}
            >
              <option value="">Undecided</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Notes on the quote</span>
            <textarea
              rows={3}
              value={form.notes ?? ''}
              placeholder="Delivery timing, who to call on arrival…"
              onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
            />
          </label>

          <p className="quotetotal">
            Total <strong>{money(afterDiscount)}</strong>
            {(form.discountPercent ?? 0) > 0 && (
              <span className="muted small"> (was {money(total)})</span>
            )}
          </p>

          <div className="rolebtns">
            <button
              type="button"
              className="btn btn--primary"
              disabled={
                busy ||
                !form.companyName.trim() ||
                !form.contactEmail.trim() ||
                form.lines.every((l) => !l.description.trim())
              }
              onClick={() =>
                run(
                  () =>
                    editing === 'new'
                      ? api.quoteCreate({
                          ...form,
                          lines: form.lines.filter((l) => l.description.trim()),
                        })
                      : api.quoteUpdate(editing, {
                          ...form,
                          lines: form.lines.filter((l) => l.description.trim()),
                        }),
                  'Quote saved as a draft. Send it when you are ready.',
                ).then(() => setEditing(null))
              }
            >
              {busy ? 'Saving…' : 'Save draft'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {quotes.length === 0 && <Alert tone="info">No quotes here yet.</Alert>}

      {quotes.map((q) => (
        <div key={q.id} className="panel">
          <h2 className="panel__title">
            {q.quoteNumber} · {q.companyName}{' '}
            <span className={`pill ${STATUS_TONE[q.status] ?? 'pill--muted'}`}>{q.status}</span>
          </h2>

          <p className="muted small">
            {q.contactName} · <a href={`mailto:${q.contactEmail}`}>{q.contactEmail}</a> ·{' '}
            <a href={`tel:${q.contactPhone}`}>{q.contactPhone}</a>
            {q.branchName ? ` · ${q.branchName}` : ''}
          </p>

          <table className="quotetable">
            <tbody>
              {q.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.description}</td>
                  <td className="quotetable__qty">{l.quantity}</td>
                  <td className="quotetable__amt">{money(l.lineTotal)}</td>
                </tr>
              ))}
              {q.discountPercent > 0 && (
                <tr>
                  <td colSpan={2}>Discount ({q.discountPercent}%)</td>
                  <td className="quotetable__amt">-{money(q.discountAmount)}</td>
                </tr>
              )}
              <tr className="quotetable__total">
                <td colSpan={2}>Total</td>
                <td className="quotetable__amt">{money(q.total)}</td>
              </tr>
            </tbody>
          </table>

          <p className="muted small">
            Valid until {q.validUntil}
            {q.hasExpired && q.status === 'Sent' ? ' · expired' : ''}
            {q.sentAt ? ` · sent ${formatDate(q.sentAt)}` : ''}
            {q.reminderCount > 0
              ? ` · chased ${q.reminderCount}×${q.lastRemindedAt ? `, last ${formatDate(q.lastRemindedAt)}` : ''}`
              : ''}
            {q.paidAt ? ` · settled ${formatDate(q.paidAt)}` : ''}
            {q.paymentReference ? ` (${q.paymentReference})` : ''}
          </p>

          {q.notes && <p className="muted small">{q.notes}</p>}

          <div className="rolebtns">
            {q.status === 'Draft' && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={busy}
                onClick={() => run(() => api.quoteSend(q.id))}
              >
                Email it
              </button>
            )}

            {q.status === 'Sent' && (
              <>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busy}
                  onClick={() => run(() => api.quoteRemind(q.id))}
                >
                  Chase now
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={busy}
                  onClick={() => {
                    const reference = window.prompt('Payment reference (optional)') ?? null;
                    void run(() => api.quoteMarkPaid(q.id, reference), 'Settled.');
                  }}
                >
                  Mark as paid
                </button>
              </>
            )}

            {q.status === 'Paid' && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={busy}
                onClick={() => run(() => api.quoteMarkFulfilled(q.id), 'Marked as delivered.')}
              >
                Mark as delivered
              </button>
            )}

            {(q.status === 'Draft' || q.status === 'Sent' || q.status === 'Expired') && canEdit && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={() => {
                  setForm({
                    companyName: q.companyName,
                    contactName: q.contactName,
                    contactEmail: q.contactEmail,
                    contactPhone: q.contactPhone,
                    lines: q.lines.map((l) => ({
                      description: l.description,
                      quantity: l.quantity,
                      unitPrice: l.unitPrice,
                    })),
                    branchId: q.branchId,
                    notes: q.notes,
                    discountPercent: q.discountPercent,
                    validUntil: q.validUntil,
                  });
                  setEditing(q.id);
                }}
              >
                Edit
              </button>
            )}

            {(q.status === 'Draft' || q.status === 'Sent') && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={busy}
                onClick={() => run(() => api.quoteDecline(q.id), 'Marked as declined.')}
              >
                Declined
              </button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
